import { Briefcase, Calendar, Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnrollment } from "@/contexts/EnrollmentContext";

export default function MyProgrammesPage() {
  const navigate = useNavigate();
  const { allEnrollments, switchProgramme, refetch } = useEnrollment();
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const handleSetPreferred = async (programmeId: string) => {
    setSwitchingId(programmeId);
    try {
      await switchProgramme(programmeId);
      await refetch();
    } catch (error) {
      console.error("Failed to set preferred programme:", error);
    } finally {
      setSwitchingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-content-foreground font-['PT_Serif']">
            My Programmes
          </h1>
          <p className="text-content-muted text-sm mt-1">
            Manage your enrolled programmes and switch between them
          </p>
        </div>
        <button
          onClick={() => navigate("/register")}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 transition-colors font-medium text-sm uppercase tracking-wider"
        >
          <Plus className="w-4 h-4" />
          Join Another Programme
        </button>
      </div>

      {allEnrollments.length === 0 ? (
        <div className="text-center py-16 bg-content-subtle border border-dashed border-content-border">
          <Briefcase className="w-12 h-12 text-content-muted mx-auto mb-4" />
          <p className="text-content-foreground mb-4">You're not enrolled in any programmes yet.</p>
          <button
            onClick={() => navigate("/register")}
            className="px-6 py-3 bg-primary text-primary-foreground hover:opacity-90 transition-colors font-medium"
          >
            Browse Available Programmes
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {allEnrollments.map((enrollment) => (
            <div
              key={enrollment.id}
              className="bg-white border border-content-border p-4 md:p-6 hover:bg-content-subtle transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-lg md:text-xl font-semibold text-content-foreground font-['PT_Serif'] truncate">
                      {enrollment.programme?.name}
                    </h3>
                    {enrollment.is_preferred && (
                      <span className="px-2 py-0.5 bg-brand/10 text-brand-foreground border border-brand/20 text-xs font-medium uppercase tracking-wider flex-shrink-0">
                        Default
                      </span>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-content-muted">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">
                        {enrollment.programme?.start_date &&
                          formatDate(enrollment.programme.start_date)}
                        {" → "}
                        {enrollment.programme?.end_date &&
                          formatDate(enrollment.programme.end_date)}
                      </span>
                    </div>
                    <div className="text-content-muted">
                      Status:{" "}
                      <span className="text-content-foreground capitalize">
                        {enrollment.status.toLowerCase()}
                      </span>
                    </div>
                    <div className="text-content-muted">
                      Progress: Week {enrollment.current_week} of{" "}
                      {enrollment.programme?.total_weeks}
                    </div>
                    <div className="text-content-muted">
                      Enrolled: {formatDate(enrollment.enrolled_at)}
                    </div>
                  </div>
                </div>

                <div className="flex sm:flex-row gap-2 lg:flex-col lg:items-end">
                  {!enrollment.is_preferred && (
                    <button
                      onClick={() => handleSetPreferred(enrollment.programme_id)}
                      disabled={switchingId !== null}
                      className="flex-1 sm:flex-none px-4 py-2 border border-content-border text-content-foreground hover:bg-content-subtle transition-colors disabled:opacity-50 font-medium text-sm min-w-[120px] flex items-center justify-center gap-2"
                    >
                      {switchingId === enrollment.programme_id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Setting...
                        </>
                      ) : (
                        "Set as Default"
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (!enrollment.is_preferred) {
                        handleSetPreferred(enrollment.programme_id);
                      }
                      navigate("/");
                    }}
                    disabled={switchingId !== null}
                    className="flex-1 sm:flex-none px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 transition-colors disabled:opacity-50 font-medium text-sm min-w-[120px]"
                  >
                    {enrollment.is_preferred ? "View Dashboard" : "Switch & View"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
